import _filter from "lodash/filter";
import _includes from "lodash/includes";
import _isEmpty from "lodash/isEmpty";
import _map from "lodash/map";
import _compact from "lodash/compact";
import { TFunction } from "i18n";
import { anyHeightRange, anyPlayingAgeRange } from "shared/config/common";
import { formatImperialLength } from "shared/helpers/formatters";
import {
  convertMetersToCentimeters,
  convertMetricToImperial,
} from "shared/helpers/unitConverters";
import { FacetCategory, IGetFacetsResponse } from "shared-types/src/Facets";
import { getFacetListByType } from "shared-utils/src/utils/facets/lists/getFacetListByType";
import { getPlayingAgeLabel } from "shared-utils/src/utils/options/getAgeRangeOptions";
import { formatListWithMore } from "pages/JobsPage/components/RolePreferences/helpers/formatListWithMore";
import { checkIsAgent } from "shared-auth";

interface MapRoleDetailsProps {
  facets: IGetFacetsResponse;
  t: TFunction;
  anyGender?: boolean;
  genderIds: number[];
  anyAppearance?: boolean;
  appearanceIds: number[];
  minPlayingAge?: number | null;
  maxPlayingAge?: number | null;
  minimumHeight?: number | null;
  maximumHeight?: number | null;
}

function mapFacetsPart({
  facets,
  category,
  ids,
  isAny,
  anyLabel,
  t,
}: {
  facets: IGetFacetsResponse;
  category: FacetCategory;
  ids: number[];
  isAny?: boolean;
  anyLabel: string;
  t: TFunction;
}): string {
  if (isAny) {
    return anyLabel;
  }

  if (_isEmpty(facets) || _isEmpty(ids)) {
    return "";
  }

  const categoryFacets = getFacetListByType(facets, category);
  const selectedFacetNames = _map(
    _filter(categoryFacets, ({ id }) => _includes(ids, id)),
    "name"
  );

  return formatListWithMore(selectedFacetNames, t);
}

function mapPlayingAgePart(
  t: TFunction,
  minPlayingAge?: number | null,
  maxPlayingAge?: number | null
): string {
  if (minPlayingAge === null && maxPlayingAge === null) {
    return "";
  }

  const min = minPlayingAge ?? anyPlayingAgeRange.min;
  const max = maxPlayingAge ?? anyPlayingAgeRange.max;

  if (min <= anyPlayingAgeRange.min && max >= anyPlayingAgeRange.max) {
    return t("common:breakdown.role.playingAge.title.any");
  }

  const minPlayingAgeLabel = getPlayingAgeLabel(min, t);
  const maxPlayingAgeLabel = getPlayingAgeLabel(max, t);

  if (min === max) {
    return minPlayingAgeLabel;
  }

  return `${minPlayingAgeLabel} ${t("common:label.to.lower")} ${maxPlayingAgeLabel}`;
}

function mapHeightPart(
  t: TFunction,
  minimumHeight?: number | null,
  maximumHeight?: number | null
): string {
  if ((minimumHeight === null && maximumHeight === null) || checkIsAgent()) {
    return "";
  }

  const min = minimumHeight ?? anyHeightRange.min;
  const max = maximumHeight ?? anyHeightRange.max;

  if (min <= anyHeightRange.min && max >= anyHeightRange.max) {
    return t("common:jobs.page.rolePreferences.anyHeight");
  }

  const minImperialRaw = convertMetricToImperial(
    convertMetersToCentimeters(min)
  );
  const maxImperialRaw = convertMetricToImperial(
    convertMetersToCentimeters(max)
  );

  const minImperial =
    minImperialRaw.inches >= 12
      ? {
          feet: minImperialRaw.feet + Math.floor(minImperialRaw.inches / 12),
          inches: minImperialRaw.inches % 12,
        }
      : minImperialRaw;

  const maxImperial =
    maxImperialRaw.inches >= 12
      ? {
          feet: maxImperialRaw.feet + Math.floor(maxImperialRaw.inches / 12),
          inches: maxImperialRaw.inches % 12,
        }
      : maxImperialRaw;

  const minHeightLabel = formatImperialLength(
    minImperial.feet,
    minImperial.inches
  );

  const maxHeightLabel = formatImperialLength(
    maxImperial.feet,
    maxImperial.inches
  );

  if (minHeightLabel === maxHeightLabel) {
    return minHeightLabel;
  }

  return t("common:jobs.page.rolePreferences.heightRange", {
    minHeight: minHeightLabel,
    maxHeight: maxHeightLabel,
  });
}

export function mapRoleDetails({
  facets,
  t,
  anyGender,
  genderIds,
  anyAppearance,
  appearanceIds,
  minPlayingAge,
  maxPlayingAge,
  minimumHeight,
  maximumHeight,
}: MapRoleDetailsProps): string {
  const parts = _compact([
    mapFacetsPart({
      facets,
      category: FacetCategory.Gender,
      ids: genderIds,
      isAny: anyGender,
      anyLabel: t("common:label.allGenders"),
      t,
    }),
    mapFacetsPart({
      facets,
      category: FacetCategory.Appearance,
      ids: appearanceIds,
      isAny: anyAppearance,
      anyLabel: t("common:label.allAppearances"),
      t,
    }),
    mapPlayingAgePart(t, minPlayingAge, maxPlayingAge),
    mapHeightPart(t, minimumHeight, maximumHeight),
  ]);

  return parts.join("\u00A0\u2022\u00A0");
}
